import * as React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {getPmPrograms, getPmSubjectNames} from "./pmProgramCatalog";
import {getTrafficTypes} from "../publishers/publishersService";

type TokenRow = {
    token: string;
    field: string;
    preview: string;
    options?: string[];
};

const SUBJECT_OPTIONS = [
    "Passes the subject name string (not an id).",
    ...getPmSubjectNames(),
];

const PROGRAM_OPTIONS = getPmPrograms().map((p) => `${p.id} — ${p.name}`);

const TWO_U_TRAFFIC_OPTIONS = [
    "Remapped for 2U partners from publisher traffic type.",
    "Facebook",
    "Email",
    "Google Ads",
];

function buildTokens(trafficTypes: string[]): TokenRow[] {
    return [
        {token: "%z%", field: "Zip", preview: "85040"},
        {
            token: "%e%",
            field: "Education level (edu_level_id)",
            preview: "3",
            options: [
                "1 — Some High School",
                "2 — GED",
                "3 — High School",
                "4 — Some College",
                "5 — Diploma/Certificate",
                "6 — Associates",
                "7 — Bachelors",
                "8 — Masters",
                "9 — PhD",
            ],
        },
        {
            token: "%mm_e%",
            field: "MediaMatchers edu remap",
            preview: "1",
            options: ["Mapped from %e% for MediaMatchers partners"],
        },
        {
            token: "%d%",
            field: "Degree sought (degree_level_id)",
            preview: "7",
            options: [
                "5 — Diploma/Certificate",
                "6 — Associates",
                "7 — Bachelors",
                "8 — Masters",
                "9 — PhD",
            ],
        },
        {token: "%hs%", field: "High school grad year", preview: "2005"},
        {
            token: "%m%",
            field: "Military",
            preview: "0",
            options: ["0 — No", "1 — Yes"],
        },
        {
            token: "%c%",
            field: "Classes / campus preference",
            preview: "1",
            options: [
                "0 — In a classroom",
                "1 — Online",
                "2 — Either way",
            ],
        },
        {
            token: "%w%",
            field: "When start (when_start_id)",
            preview: "2",
            options: [
                "2 — Immediately",
                "4 — 1–3 months",
                "8 — 4–6 months",
                "16 — 6+ months",
            ],
        },
        {
            token: "%kw%",
            field: "Area of interest / subject name",
            preview: "Business",
            options: SUBJECT_OPTIONS,
        },
        {
            token: "%p%",
            field: "Program id",
            preview: "21",
            options: PROGRAM_OPTIONS,
        },
        {token: "%a%", field: "Street address", preview: "1234 Main st"},
        {token: "%publisher_id%", field: "Publisher id", preview: "123"},
        {token: "%lead_id%", field: "Lead id", preview: "4581167"},
        {token: "%id%", field: "Portal redirect id", preview: "(campaign id)"},
        {token: "%email%", field: "Email", preview: "test@gmail.com"},
        {token: "%first_name%", field: "First name", preview: "Test"},
        {token: "%last_name%", field: "Last name", preview: "Test"},
        {token: "%phone%", field: "Phone", preview: "6025551212"},
        {token: "%state%", field: "State", preview: "AZ"},
        {
            token: "%traffic_type%",
            field: "Traffic type",
            preview: "Facebook",
            options: trafficTypes.length ? trafficTypes : ["Loading…"],
        },
        {token: "%cid%", field: "Creative id", preview: "600f2e6610cba500163c2203"},
        {token: "%2u_url%", field: "2U source domain", preview: "www.degreelocate.com"},
        {
            token: "%2u_traffic_type%",
            field: "2U traffic type",
            preview: "Facebook",
            options: TWO_U_TRAFFIC_OPTIONS,
        },
    ];
}

function OptionsList({options}: {options?: string[]}) {
    if (!options?.length) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    const long = options.length > 12;
    return (
        <Box
            component="ul"
            sx={{
                m: 0,
                pl: 2,
                maxHeight: long ? 220 : "none",
                overflowY: long ? "auto" : "visible",
            }}
        >
            {options.map((opt) => (
                <Typography key={opt} component="li" variant="body2" sx={{mb: 0.25}}>
                    {opt}
                </Typography>
            ))}
        </Box>
    );
}

type Props = {
    open: boolean;
    onClose: () => void;
};

export default function PrLinkTokensModal({open, onClose}: Props) {
    const [trafficTypes, setTrafficTypes] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        let cancelled = false;
        (async () => {
            const types = await getTrafficTypes();
            if (!cancelled) {
                setTrafficTypes(types || []);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open]);

    if (!open) {
        return null;
    }

    const tokens = buildTokens(trafficTypes);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Link-out tokens</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{mb: 1.5}}>
                    Use these placeholders in Linked landing pages. Values are substituted from the lead at redirect time.
                    We pass our IDs/values — partners map them on their side.
                </Typography>
                <Box sx={{overflowX: "auto"}}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700, whiteSpace: "nowrap"}}>Token</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Field</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Preview sample</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Options / notes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tokens.map((t) => (
                                <TableRow key={t.token} sx={{verticalAlign: "top"}}>
                                    <TableCell sx={{fontFamily: "monospace", whiteSpace: "nowrap"}}>{t.token}</TableCell>
                                    <TableCell>{t.field}</TableCell>
                                    <TableCell sx={{fontFamily: "monospace", whiteSpace: "nowrap"}}>{t.preview}</TableCell>
                                    <TableCell sx={{minWidth: 280}}>
                                        <OptionsList options={t.options}/>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
